/**
 * Root reducer: the single entry point `liveGameStore.applyMessage` calls.
 *
 * Contract:
 *  - EVERY type in `messages.INBOUND` is routed here (the routing table below
 *    is built from INBOUND, so a new message type is a compile-visible gap,
 *    not a silent drop);
 *  - it is pure — one new state object, no timers, no React;
 *  - `now` is injected for deterministic tests.
 */
import {INBOUND, FAMILY_OF, MessageFamily} from '../messages';
import {LiveGameState} from '../state';
import {GAME_TYPES, gameReducer} from './game';
import {CHALLENGE_TYPES, challengeReducer} from './challenge';
import {TOURNAMENT_TYPES, tournamentReducer} from './tournament';
import {ARENA_TYPES, arenaReducer} from './arena';
import {WATCH_TYPES, watchReducer} from './watch';

/**
 * type -> family, for every inbound type. Unknown types fall through to the
 * meta-only branch (nothing in the app sends them, but the store must not
 * throw on one).
 */
export const ROUTES: {[type: string]: MessageFamily} = Object.keys(
  INBOUND,
).reduce((acc: {[type: string]: MessageFamily}, type) => {
  acc[type] = FAMILY_OF[type] || 'game';
  return acc;
}, {});

/**
 * The inbound types whose reducer is REAL (not the meta-only milestone stub).
 * The handler-parity test allows anything outside this list only while it is
 * in `../parity/allowlist.ts`, which shrinks per milestone. A3a made the
 * tournament, watch and arena families real, so the only inbound entries left
 * on the allow-list are the three ECHOES of our own requests.
 */
export const HANDLED_INBOUND: string[] = [
  ...GAME_TYPES,
  ...CHALLENGE_TYPES,
  ...TOURNAMENT_TYPES,
  ...WATCH_TYPES,
  ...ARENA_TYPES,
];

export function reduce(
  state: LiveGameState,
  type: string,
  data: any,
  now: number = Date.now(),
): LiveGameState {
  const stamped: LiveGameState = {
    ...state,
    meta: {...state.meta, lastMessageType: type, lastMessageAt: now},
  };

  switch (ROUTES[type]) {
    case 'game':
      return gameReducer(stamped, type, data, now);
    case 'challenge':
      return challengeReducer(stamped, type, data, now);
    case 'tournament':
      return tournamentReducer(stamped, type, data, now);
    case 'arena':
      return arenaReducer(stamped, type, data, now);
    case 'watch':
      return watchReducer(stamped, type, data, now);
    default:
      return stamped;
  }
}
